const debug = require('debug')('knack.api-wrapper');
const _ = require('lodash');
const axios = require('axios').default;
const axiosCookieJar = require('axios-cookiejar-support');
const tough = require('tough-cookie');
const FormData = require('form-data');
const isRunningNodeJs = require('detect-node');
const axiosRetry = require('axios-retry').default;

const builderMethods = require('./builder');

let fs;
let readFile;
let promisify;
const isLocalhostRegex = /^http:\/\/localhost:3000\/v1\/$/;

if (isRunningNodeJs) {
  promisify = require('util').promisify;
  fs = require('fs');
  readFile = promisify(fs.readFile);

  axiosCookieJar.wrapper(axios);
}

module.exports = class KnackApiWrapper {
  constructor(settings) {
    this.mode = settings.mode || 'builder';
    this.email = settings.email;
    this.account_id = settings.account_id;
    this.application_id = settings.application_id;
    this.api_key = settings.api_key;
    this.api_uri = settings.api_uri;
    this.useRetries = settings.useRetries || false;
    this.retryOptions = settings.retryOptions || {};

    // setting this value will override the "foo.bar" portion of  "Domain=foo.bar;" within the set-cookie header value
    // this is used for local dev env container-to-container communication where
    // the consuming container will refer to the Server API service as api:3000 but
    // the Server API will be on domain knackdev.com
    // replacing the domain in the set-cookie header value will allow the cookie to be set
    // and used in subsequent requests
    this.setCookieHeaderDomainValueOverride = settings.setCookieHeaderDomainValueOverride;
    this.builderVersion = settings.builderVersion || 'v3';
    this.headers = {
      'x-knack-new-builder': true,
    };
    this.axios = axios.create({
      baseURL: this.api_uri,
      timeout: settings.timeout || 601000, // default to 1 second longer than our 60 second API timeout
    });

    if (this.useRetries) {
      axiosRetry(this.axios, {
        retries: 3,
        ...this.retryOptions,
      });
    }

    this.axios.interceptors.request.use(this.requestInterceptor.bind(this));
    this.axios.interceptors.response.use(this.responseInterceptor, this.errorInterceptor.bind(this));

    this._is_authenticated = false;

    this._writeEndpointHttpMethods = [
      'delete',
      'post',
      'put',
    ];

    this._updateInternals(this.account_id, this.application_id, this.api_key);

    this.modeToHeaderManagementFunctionMapping = {
      builder: this.addBuilderHeadersToInterceptedRequest.bind(this),
      dashboard: this.addDashboardHeadersToInterceptedRequest.bind(this),
      renderer: this.addRendererHeadersToInterceptedRequest.bind(this),
      api: this.addApiHeadersToInterceptedRequest.bind(this),
    };

    this.builder = {
      getApplication: builderMethods.getApplication.bind(this),
      getApplicationPages: builderMethods.getApplicationPages.bind(this),
    };
  }

  async addDashboardHeadersToInterceptedRequest(configHeaders) {
    debug('addDashboardHeadersToInterceptedRequest()');

    const headers = { ...configHeaders };

    // send in entire config param and check config.data before setting this one?
    // could also check if PUT or POST?

    // this header required until we remove legacy code from Server repo
    headers['x-knack-new-builder'] = true;

    headers['X-Knack-REST-API-Key'] = 'dashboard';
    headers['X-Knack-Application-Id'] = 'dashboard';

    return headers;
  }

  async addRendererHeadersToInterceptedRequest(configHeaders) {
    debug('addRendererHeadersToInterceptedRequest()');

    const headers = { ...configHeaders };

    return headers;
  }

  async addBuilderHeadersToInterceptedRequest(configHeaders) {
    debug('addBuilderHeadersToInterceptedRequest()');

    const headers = { ...configHeaders };

    // send in entire config param and check config.data before setting this one?
    // could also check if PUT or POST?

    // this header required until we remove legacy code from Server repo
    headers['x-knack-new-builder'] = true;

    headers['X-Knack-REST-API-Key'] = 'builder';

    if (this.application_id) {
      headers['X-Knack-Application-Id'] = this.application_id;
    }

    return headers;
  }

  async addApiHeadersToInterceptedRequest(configHeaders) {
    debug('addApiHeadersToInterceptedRequest()');

    const headers = { ...configHeaders };

    if (_.isNil(this.api_key)) {
      await this._setApiKey();
    }

    // this header required until we remove legacy code from Server repo
    headers['x-knack-new-builder'] = true;

    headers['X-Knack-REST-API-Key'] = this.api_key;

    if (this.application_id) {
      headers['X-Knack-Application-Id'] = this.application_id;
    }

    return headers;
  }

  async requestInterceptor(config) {
    debug('requestInterceptor()');

    debug('initial config', config);

    if (config.interceptHeaders !== false) {
      debug('modifying headers based on mode:', config.mode || this.mode);

      // if config.mode specified, use it otherwise use whatever the class is set to
      config.headers = await this.modeToHeaderManagementFunctionMapping[config.mode || this.mode](config.headers);
    }

    if (config.ignoreCredentials !== true) {
      debug('adding withCredentials: true and jar: this.cookie_jar to request config');

      config.withCredentials = true;
      config.jar = this.cookie_jar;
    }

    if (!config.params) {
      config.params = {};
    }

    debug(`adding x-knack-builder-version, set to ${this.builderVersion}`);
    config.headers['x-knack-builder-version'] = this.builderVersion;

    if (_.isEmpty(this._clientSubdomainMap)) {
      debug('this._clientSubdomainMap is empty, returning config:', config);

      return config;
    }

    // TEMP HACK: If trying to use staging infra, don't mess with read/write endpoint splitting (client subdomain mappings)
    if (config.baseURL.includes('knack-api-us-east-1-staging')) {
      return config;
    }

    debug('this._clientSubdomainMap', this._clientSubdomainMap);

    // Not using a URL parsing module since our usage would
    // be so simple it would be overkill at this time
    // Would also need node & browser compat which means using
    // a third party library which is more overkill + bundle size increase
    const protocol = config.baseURL.split('/')[0];

    // Not currently supporting multiple read or write endpoints
    const subDomain = this._writeEndpointHttpMethods.includes(config.method) ? this._clientSubdomainMap.write[0] : this._clientSubdomainMap.read[0];

    debug('using subdomain:', subDomain);

    debug('config.baseURL BEFORE replacing subdomain', config.baseURL);

    if (!isLocalhostRegex.test(config.baseURL)) {
      // Assumes that we're replacing the first portion of domain (e.g., replacing "api" in api.knack.com)
      config.baseURL = `${protocol}//${config.baseURL.replace(config.baseURL.split('.')[0], subDomain)}`;
    }

    debug('config.baseURL AFTER replacing subdomain', config.baseURL);

    debug('final config being returned:', config);

    return config;
  }

  // eslint-disable-next-line class-methods-use-this
  responseInterceptor(response) {
    if (_.hasIn(response, 'config.resolveWithFullResponse') && response.config.resolveWithFullResponse)  {
      return response;
    }
    const numberOfRetries = _.get(response, 'config["axios-retry"].retryCount', 0);

    if (numberOfRetries > 0) {
      if (!_.hasIn(response, 'config._retryCounter')) {
        // eslint-disable-next-line no-underscore-dangle
        response.config._retryCounter = numberOfRetries;
      }
      let retryCounter = _.get(response, 'config._retryCounter', 0);
      if (retryCounter > 0) {
        retryCounter -= 1;
        // eslint-disable-next-line no-underscore-dangle
        response.config._retryCounter = retryCounter;
        return response;
      }
    }

    return response.data;
  }

  errorInterceptor(error) {
    debug('errorInterceptor(), error:');
    debug(error);

    // Network Error
    if (_.isNil(error.response)) {
      debug('errorInterceptor: error.response was nil, rejecting promise');

      return Promise.reject(error);
    }

    if (_.hasIn(error, 'response') && error.response.status === 401 && error.response.data === 'Access Error: No User Registered') {
      debug('errorInterceptor: setting this._is_authenticated to false');

      this._is_authenticated = false;

      debug('errorInterceptor: returning "reauthenticate"');

      // todo extend Error and return an instance of AuthenticationError?
      return 'reauthenticate';
    }

    // Transform if it isn't in the format of Knack.sendErrors, an object with an errors property that's an array of objects
    if (_.hasIn(error, 'response.data')) {
      const errorData = error.response.data;

      if (_.isObject(errorData)) {
        if (!_.hasIn(errorData, 'errors')) {
          error.response.data = {
            errors: [errorData],
          };
        } else if (_.hasIn(errorData, 'errors') && !_.isArray(errorData.errors)) {
          error.response.data = {
            errors: [errorData],
          };
        }
      } else if (_.isArray(errorData)) {
        error.response.data = {
          errors: errorData,
        };
      } else {
        const newError = {
          message: errorData,
        };

        error.response.data = {
          errors: [newError],
        };
      }

      error.response.data.status = error.response.status;
    }

    // Without this Promise.reject, axios won't throw an error.  It's how it's done in its docs
    return Promise.reject(error.response.data);
  }

  _initCookieJar() {
    if (!this._cookie_jar) {
      this._cookie_jar = new tough.CookieJar();
      this._cookie_jar.rejectPublicSuffixes = false;
    }

    return this._cookie_jar;
  }

  get cookie_jar() {
    return this._initCookieJar();
  }

  _setCookie(cookieString, apiUrl) {
    return new Promise((resolve, reject) => {
      this.cookie_jar.setCookie(tough.Cookie.parse(cookieString), apiUrl, (err, cookie) => {
        if (err) {
          reject(err);
        }

        resolve(cookie);
      });
    });
  }

  _getCookies(apiUrl) {
    return new Promise((resolve, reject) => {
      this.cookie_jar.getCookies(apiUrl, (err, cookies) => {
        if (err) {
          reject(err);
        }

        resolve(cookies);
      });
    });
  }

  _updateInternals(account_id, application_id, api_key) {
    if (!_.isNil(account_id)) {
      this.account_id = account_id;
    }

    if (!_.isNil(application_id)) {
      this.application_id = application_id;
    }

    if (!_.isNil(api_key)) {
      this.api_key = api_key;
    }

    if (!_.isNil(this.application_id)) {
      this.headers['X-Knack-Application-Id'] = this.application_id;
    }

    if (!_.isNil(this.api_key)) {
      this.headers['X-Knack-REST-API-Key'] = this.api_key;
    }

    return this.headers;
  }

  _removeEmptyAttributes(obj) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined) {
        delete obj[key];
      }
    });
  }

  async isAuthenticated() {
    debug('isAuthenticated()');

    const isAuthenticated_options = {
      url: 'authenticated',
      method: 'GET',
      resolveWithFullResponse: true,
    };

    const isAuthenticated_response = await this.axios(isAuthenticated_options);
    const authenticatedResponseData = isAuthenticated_response.data;

    if (!_.isNil(authenticatedResponseData) && _.isObject(authenticatedResponseData) && authenticatedResponseData.authenticated) {
      this.account_id = authenticatedResponseData.account_id;
      this.user_id = authenticatedResponseData.user_id;
      this.email = authenticatedResponseData.user_email;
      this.app_rights = authenticatedResponseData.app_rights;
      this.first_name = authenticatedResponseData.first_name;
      this.last_name = authenticatedResponseData.last_name;
      this.avatar_uri = authenticatedResponseData.avatar_uri;
    }

    this._is_authenticated = authenticatedResponseData.authenticated;

    return this._is_authenticated;
  }

  async logout() {
    debug('logout()');

    const options = {
      url: `accounts/${this.account_id}/session/${this.user_id}`,
      method: 'DELETE',
      mode: 'dashboard',
    };

    this._is_authenticated = false;

    return this.axios(options);
  }

  async sendUserIntros(objectKey, userIds) {
    const options = {
      url: `objects/${objectKey}/records/users/intros`,
      method: 'POST',
      data: {
        ids: userIds,
      },
    };

    return this.axios(options);
  }

  async resetUserPasswords(objectKey, userIds) {
    const options = {
      url: `objects/${objectKey}/records/users/passwords`,
      method: 'POST',
      data: {
        ids: userIds,
      },
    };

    return this.axios(options);
  }

  async updateUsersStatus(objectKey, userIds, { accountStatus, sendApprovalEmail }) {
    const options = {
      url: `objects/${objectKey}/records/users/status`,
      method: 'POST',
      data: {
        ids: userIds,
        account_status: accountStatus,
        send_email_approval: sendApprovalEmail,
      },
    };

    return this.axios(options);
  }

  async forgotPassword(data) {
    const options = {
      url: 'accounts/users/password',
      method: 'POST',
      data,
      mode: 'dashboard',
    };

    return this.axios(options);
  }

  async _setApiKey(application_id = this.application_id) {
    const options = {
      url: 'api_key',
      method: 'GET',
    };

    const { api_key } = await this.axios(options);

    this._updateInternals(this.account_id, this.application_id, api_key);
  }

  async getApiKey() {
    debug('getApiKey()');

    if (_.isNil(this.api_key)) {
      await this._setApiKey();
    }

    return this.api_key;
  }

  getSSORedirectURL(authProtocol, returnUrl) {
    const stateVars = `p=${authProtocol}`; // &s=${pageKey}&h=${hash?}

    // note: return url can't include fragments
    return `${this.api_uri}applications/${this.application_id}/auth/${authProtocol}?returnUrl=${encodeURIComponent(returnUrl)}&state=${encodeURIComponent(stateVars)}`;
  }

  async getSSOUserProfile(strategyName, returnUrl, code) {
    const options = {
      url: `applications/${this.application_id}/auth/${strategyName}/gettestprofile?returnUrl=${returnUrl}&code=${code}`,
      method: 'POST',
    };

    return this.axios(options);
  }

  async getNewApiKey() {
    debug('getNewApiKey()');

    const options = {
      url: 'api_key',
      method: 'POST',
    };

    const { api_key } = await this.axios(options);

    this._updateInternals(this.account_id, this.application_id, api_key);

    return this.api_key;
  }

  async getApiUsage() {
    debug('getApiUsage()');

    const options = {
      url: `accounts/${this.account_id}/usage/api`,
      method: 'GET',
    };

    return (await this.axios(options)).results;
  }

  /**
   *
   * @param {string} accountSlug - Account slug
   * @param {string} appName - Application name
   * @param {string} email - Email
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @param {string} password - User password
   * @param {boolean} [isSql=true] - Is sql application flag. Default true.
   * @param {string} [appBuilderPlan='knack_internal'] - Builder plan. Default 'knack_internal'.
   * @param {object} [options={}] - Additional options for account creation.
   * @param {boolean} [options.use_account_db=false] - Flag indicating whether or not to use consolidated account db. Default false.
   * @returns {object}
   */
  async createAccount(accountSlug, appName, email, firstName, lastName, password, isSql = true, appBuilderPlan = 'knack_internal', options = {}) {
    const config = {
      url: 'accounts/register',
      method: 'POST',
      data: {
        account: accountSlug,
        app_name: appName,
        email,
        first_name: firstName,
        last_name: lastName,
        password,
        is_sql: isSql,
        appBuilderPlan,
        use_account_db: !!options.useAccountDb,
      },
      resolveWithFullResponse: true,
      interceptHeaders: false,
      ignoreCredentials: true,

      // If we want to be consistent with current account signup, we'd do this but it seems silly
      // tested local account signup and it sent dashboard headers so setting mode to dashboard here :shrug:
      // mode: `dashboard`
    };

    const login_response = await this.axios(config);

    if (isRunningNodeJs) {
      // TODO: createAccount had this logic added to it but authenticate has the same logic
      // we won't know which is called first so we are extra defensive
      // if already authenticated, we'll have data.account.id but no set-cookie
      // if NOT already authenticated, we'll have data.account.id AND set-cookie
      if (!_.hasIn(login_response, 'data.account.id') && (!_.hasIn(login_response, 'headers.set-cookie') || login_response.headers['set-cookie'].length === 0)) {
        throw new Error('Invalid credentials');
      }

      // TODO: authenticate also has this logic for some reason so we are extra defensive
      // if already authenticated, we will NOT have set-cookie
      // if NOT already authenticated, we WILLhave set-cookie
      if (_.hasIn(login_response, 'headers.set-cookie') && login_response.headers['set-cookie'].length !== 0) {
        if (this.setCookieHeaderDomainValueOverride) {
          login_response.headers['set-cookie'][0] = login_response.headers['set-cookie'][0].replace(/Domain=.+?;/, `Domain=${this.setCookieHeaderDomainValueOverride};`);
        }
        await this._setCookie(login_response.headers['set-cookie'][0], this.api_uri);
      }
    }

    if (!_.hasIn(login_response, 'data.account_id')) {
      throw new Error('Invalid credentials');
    }

    this._is_authenticated = true;
    this.user_id = login_response.data.user.id;
    this.account_id = login_response.data.account_id;
    this.application_id = login_response.data.application_id;

    this._updateInternals(this.account_id, this.application_id);

    const api_key = await this.getApiKey();

    if (_.isNil(api_key)) {
      throw new Error('Failed to retrieve newly created application\'s api key.');
    }

    return login_response.data;
  }

  async deleteAccount(feedback) {
    if (!this.account_id) {
      throw new Error('account_id is required');
    }

    if (!this.user_id) {
      throw new Error('user_id is required');
    }

    const options = {
      url: `accounts/${this.account_id}/delete/${this.user_id}`,
      method: 'PUT',
      data: {
        id: this.user_id,
        feedback,
      },
      mode: 'dashboard',
    };

    this._is_authenticated = false;

    return this.axios(options);
  }

  async authenticate(email, password, totp_token) {
    debug('authenticate(), this._is_authenticated:', this._is_authenticated);

    if (this._is_authenticated) {
      return;
    }

    if (!_.isNil(email)) {
      this.email = email;
    }

    const login_options = {
      url: 'accounts/session/',
      method: 'POST',
      data: {
        email: this.email,
        password,
        totp_token,
      },
      resolveWithFullResponse: true,
      mode: 'dashboard',
    };

    const login_response = await this.axios(login_options);
    debug('login_response: ', login_response);

    // Browsers do not have access to set-cookie header so only setup cookie_jar if we're in a nodejs env
    if (isRunningNodeJs) {
      // TODO: createAccount also has this logic for some reason so we are extra defensive
      // if already authenticated, we'll have data.account.id but no set-cookie
      // if NOT already authenticated, we'll have data.account.id AND set-cookie
      if (!_.hasIn(login_response, 'data.account.id') && (!_.hasIn(login_response, 'headers.set-cookie') || login_response.headers['set-cookie'].length === 0)) {
        throw new Error('Invalid credentials');
      }

      // TODO: createAccount also has this logic for some reason so we are extra defensive
      // if already authenticated, we will NOT have set-cookie
      // if NOT already authenticated, we WILLhave set-cookie
      if (_.hasIn(login_response, 'headers.set-cookie') && login_response.headers['set-cookie'].length !== 0) {
        if (this.setCookieHeaderDomainValueOverride) {
          login_response.headers['set-cookie'][0] = login_response.headers['set-cookie'][0].replace(/Domain=.+?;/, `Domain=${this.setCookieHeaderDomainValueOverride};`);
        }
        await this._setCookie(login_response.headers['set-cookie'][0], this.api_uri);
      }
    }

    // Return 2FA check
    if (_.get(login_response, 'data.using_2fa') === true) {
      return login_response.data;
    }

    if (!_.hasIn(login_response, 'data.account.id')) {
      throw new Error('Invalid credentials');
    }

    this._is_authenticated = true;
    this.user_id = login_response.data.account.user_id;
    this.account_id = login_response.data.account.id;
    this.app_rights = login_response.data.app_rights;

    return login_response;
  }

  async liveAppLogin(email, password) {
    const options = {
      url: 'session',
      method: 'POST',
      data: {
        email,
        password,
      },
    };

    return this.axios(options);
  }

  async getBatchRecordsCount(objectKey, criteria) {
    const options = {
      url: `objects/${objectKey}/batch/count`,
      method: 'POST',
      data: {
        criteria,
        type: 'query',
      },
    };

    return this.axios(options);
  }

  async getConnections(object_key, filters = [], recordsPerPage = 2000, limitReturn = true) {
    let queryString = `?rows_per_page=${recordsPerPage}`;

    if (!_.isEmpty(filters)) {
      queryString += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }

    if (limitReturn === true) {
      queryString += `&limit_return=${limitReturn}`;
    }

    const options = {
      url: `applications/${this.application_id}/connections/${object_key}${queryString}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getRecordIdentifiers(objectKey, recordIds) {
    return this.axios({
      url: `applications/${this.application_id}/objects/${objectKey}/identifiers`,
      method: 'GET',
      params: {
        ids: recordIds,
      },
    });
  }

  async getRecords(object_key, filters = {}, search = '', page = 1, records_per_page = 25, sortField = '', sortOrder = 'asc') {
    debug('getRecords()');

    const options = {
      url: `objects/${object_key}/records?filters=${encodeURIComponent(JSON.stringify(filters))}&search=${encodeURIComponent(search)}&rows_per_page=${records_per_page}&page=${page}&image_thumbs=true&format=both`,
      method: 'GET',
    };

    if (sortField) {
      options.url = `${options.url}&sort_field=${sortField}&sort_order=${sortOrder}`;
    }

    return this.axios(options);
  }

  async setPreviewUser(sceneKey, previewUser) {
    debug('setPreviewUser()');

    if (_.isNil(sceneKey) || _.isNil(previewUser)) {
      throw new Error('sceneKey or previewUser was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${sceneKey}/set_preview_user/`,
      method: 'POST',
      data: {
        preview_user: previewUser,
      },
    };

    return this.axios(options);
  }

  async getRecordsForReport(scene_key, view_key, filters = {}, page = 1, records_per_page = 25) {
    debug('getRecordsForReport()');

    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/report?filters=${encodeURIComponent(JSON.stringify(filters))}&rows_per_page=${records_per_page}&page=${page}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getRecordsForReportPreview(pageKey, viewKey, filters = {}, page = 1, records_per_page = 25, report) {
    debug('getRecordsForReportPreview()');

    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${pageKey}/views/${viewKey}/report`,
      data: {
        preview: true,
        format: 'both',
        report,
        filters,
        records_per_page,
        page,
      },
      method: 'POST',
    };

    return this.axios(options);
  }

  /**
   *
   * @param {string} scene_key
   * @param {string} view_key
   * @param {any} filters
   * @param {number?} page
   * @param {number?} records_per_page
   * @param {string?} sortField
   * @param {'asc' | 'desc'?} sortOrder
   * @param {Array<string>?} pageParentRecordIds
   * @param {string?} search keyword search string
   * @returns
   */
  async getRecordsForView(scene_key, view_key, filters, page = 1, records_per_page = 25, sortField = '', sortOrder = 'asc', pageParentRecordIds = [],  search = '') {
    debug('getRecordsForView()');

    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/records?filters=${encodeURIComponent(JSON.stringify(filters || {}))}&search=${encodeURIComponent(search)}&rows_per_page=${records_per_page}&page=${page}`,
      method: 'GET',
    };

    if (sortField) {
      options.url = `${options.url}&sort_field=${sortField}&sort_order=${sortOrder}`;
    }

    if (!_.isNil(pageParentRecordIds)) {
      for (const pageParentRecordId of pageParentRecordIds) {
        if (_.isNil(pageParentRecordIds.value)) {
          continue;
        }

        options.url = `${options.url}&${pageParentRecordId.key}=${pageParentRecordId.value}`;
      }
    }

    return this.axios(options);
  }

  async getFakeRecordsForView(scene_key, view_key, records_per_page = 10) {
    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/fakes?rows_per_page=${records_per_page}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getRecordForView(scene_key, view_key, record_id) {
    debug('getRecordForView()');

    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/records/${record_id}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getFakeRecordForView(scene_key, view_key) {
    debug('getFakeRecordForView()');

    if (_.isNil(this.application_id)) {
      throw new Error('this.application_id was null or undefined but is required for this operation');
    }

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/fake`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getRecord(object_key, record_id, format = 'both') {
    debug('getRecord()');

    const options = {
      url: `objects/${object_key}/records/${record_id}?format=${format}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async updateRecord(object_key, record_id, data, assets) {
    debug('updateRecord()');

    // TODO: create assets updates from data
    if (!_.isEmpty(assets)) {
      data = await this.createAssets(assets, data);
    }

    const options = {
      url: `objects/${object_key}/records/${record_id}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateRecordByView(scene_id, view_id, record_id, data, assets) {
    debug('updateRecord()');

    // TODO: create assets updates from data
    if (!_.isEmpty(assets)) {
      data = await this.createAssets(assets, data);
    }

    const options = {
      url: `scenes/${scene_id}/views/${view_id}/records/${record_id}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateBatchRecords(objectKey, criteria, values) {
    debug('updateBatchRecords()');

    const data = {
      ids: {},
      type: 'query',
      criteria,
      values,
    };

    const options = {
      url: `objects/${objectKey}/batch`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async updateBatchRecordsById(objectKey, recordIds, values) {
    debug('updateBatchRecordsById()');

    const data = {
      ids: recordIds,
      criteria: {},
      type: 'ids',
      values,
    };

    const options = {
      url: `objects/${objectKey}/batch`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async deleteRecord(object_key, record_id) {
    debug('deleteRecord()');

    const options = {
      url: `objects/${object_key}/records/${record_id}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async deleteBatchRecords(objectKey, rules, groups = [], match = 'all') {
    debug('deleteBatchRecords()');
    debug('rules:', rules);
    debug('groups:', groups);

    const data = {
      ids: {},
      criteria: {
        match,
        rules,
        groups,
      },
      type: 'query',
    };

    const options = {
      url: `objects/${objectKey}/records/delete`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async deleteBatchRecordsById(objectKey, recordIds) {
    debug('deleteBatchRecordsById()');

    const data = {
      ids: recordIds,
    };

    const options = {
      url: `objects/${objectKey}/records/delete`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createImageAsset(asset) {
    debug('createImageAsset()');

    if (_.isNil(asset)) {
      return;
    }

    const { content, filename } = asset;

    const form = new FormData();

    form.append('files', content, filename);

    const options = {
      url: `applications/${this.application_id}/assets/image/upload`,
      method: 'POST',
      data: form,
      resolveWithFullResponse: true,
    };

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    return this.axios(options);
  }

  // makes requests to create assets and then merges the
  // response asset ids with initial field keys
  // TODO: server should just be able to handle fields w/ formdata info
  // to create assets
  async createAssets(assets, recordData = {}) {
    debug('createAssets()');

    const assetsResponses = await Promise.all(assets.map(async (asset) => {
      // TODO: hacky way to handle resetting blank values
      if (_.isNil(asset.content)) {
        return {
          data: {
            id: '',
          },
        };
      }

      const {
        type, content, filename, fieldKey,
      } = asset;
      const { size, type: fileType } = content;

      const form = new FormData();

      form.append('files', content, filename);

      const options = {
        url: `applications/${this.application_id}/assets/${type}/upload/stream`,
        method: 'POST',
        params: {
          fieldKey,
          filename,
          size,
          type: fileType,
        },
        data: form,
        resolveWithFullResponse: true,
      };

      if (_.isFunction(form.getHeaders)) {
        options.headers = { ...form.getHeaders() };
      }

      const data = await this.axios(options);

      // As long as each asset is uploaded one at a time, we need to get the first result from the response array.
      data.data = data.data[0];

      return data;
    }));

    // mapping asset response id to field key via index of initial asset array
    const fieldsWithAssetId = assetsResponses.reduce((assetDataObject, assetResponse, index) => {
      const assetFieldKey = assets[index].fieldKey;

      assetDataObject[assetFieldKey] = assetResponse.data.id;

      return assetDataObject;
    }, {});

    return { ...recordData, ...fieldsWithAssetId };
  }

  async createRecord(object_key, data, assets) {
    debug('createRecord()');

    if (!_.isEmpty(assets)) {
      data = await this.createAssets(assets, data);
    }

    const options = {
      url: `objects/${object_key}/records`,
      method: 'POST',
      data,
    };

    if (!_.isEmpty(assets)) {
      options.params = {
        image_thumbs: true,
      };
    }

    return this.axios(options);
  }

  async bulkCreateRecords(data) {
    debug('bulkCreateRecords()');

    const options = {
      url: 'records',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async bulkUpdateRecords(data) {
    debug('bulkUpdateRecords()');

    const options = {
      url: 'records',
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async createRecordByView(scene_key, view_key, data) {
    debug('createRecordByView()');

    const options = {
      url: `scenes/${scene_key}/views/${view_key}/records`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createPage(name = 'New Page', type = 'page', views = [], authenticated = false, login_vars = {}, menu_pages = null, parent = null) {
    debug('createPage()');

    const options = {
      url: 'scenes',
      method: 'POST',
      data: {
        name,
        type,
        views,
        authenticated,
        login_vars,
        menu_pages,
        parent,
      },
    };

    return this.axios(options);
  }

  async createPages(pages) {
    debug('createPages()');

    const options = {
      url: 'scenes/bulk',
      method: 'POST',
      data: pages,
    };

    return this.axios(options);
  }

  async updatePages(pages) {
    debug('updatePages()');

    const options = {
      url: 'scenes/bulk',
      method: 'PUT',
      data: pages,
    };

    return this.axios(options);
  }


  async createLoginPage(name = 'New Login Page', views = [], login_vars = {}) {
    debug('createLoginPage()');

    const options = {
      url: 'scenes',
      method: 'POST',
      data: {
        name,
        views,
        authenticated: false,
        login_vars,
      },
    };

    return this.axios(options);
  }

  async createUserPage(name = 'New Page', type = 'page', views = [], authenticated = false, login_vars = {}, menu_pages = null) {
    debug('createUserPage()');

    const options = {
      url: 'scenes',
      method: 'POST',
      data: {
        name,
        type,
        views,
        authenticated,
        ...login_vars,
        menu_pages,
      },
    };

    return this.axios(options);
  }

  async copyPage(pageKey, newPageName) {
    debug('copyPage()');

    const options = {
      url: `scenes/${pageKey}/copy`,
      method: 'POST',
      data: {
        name: newPageName,
      },
    };

    return this.axios(options);
  }

  async deletePage(pageKey) {
    debug('deletePage()');

    const options = {
      url: `scenes/${pageKey}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async updatePage(pageKey, name, views, authenticated, allowed_profiles, limit_profile_access, slug, modal, modal_prevent_background_click_close, page_menu_display, ignore_entry_scene_menu, icon, print) {
    debug('updatePage()');

    const options = {
      url: `scenes/${pageKey}`,
      method: 'PUT',
      data: {
        name,
        authenticated,
        allowed_profiles,
        limit_profile_access,
        views,
        slug,
        modal,
        modal_prevent_background_click_close,
        page_menu_display,
        ignore_entry_scene_menu,
        icon,
        print,
      },
    };

    return this.axios(options);
  }

  async updatePageRules(pageKey, rules) {
    debug('updatePageRules()');

    const options = {
      url: `scenes/${pageKey}/rules`,
      method: 'POST',
      data: {
        rules,
      },
    };

    return this.axios(options);
  }

  async updatePageSettings(pageKey, data = {}) {
    debug('updatePageSettings()');

    const options = {
      url: `scenes/${pageKey}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updatePageLoginSettings(pageKey, views, authenticated, limit_profile_access, allowed_profiles) {
    debug('updatePageLoginSettings()');

    const options = {
      url: `scenes/${pageKey}`,
      method: 'PUT',
      data: {
        views,
        authenticated,
        limit_profile_access,
        allowed_profiles,
      },
    };

    return this.axios(options);
  }

  async updatePagesSort(order) {
    debug('updatePagesSort()');

    const options = {
      url: 'scenes/sort',
      method: 'POST',
      data: {
        order,
      },
    };

    return this.axios(options);
  }

  async updatePageSort({ pageKey, newIndex }) {
    debug('updatePageSort()');

    const options = {
      url: `scenes/sort/${pageKey}`,
      method: 'POST',
      data: {
        newIndex,
      },
    };

    return this.axios(options);
  }

  async clearAllRecords(objectKey) {
    debug('clearAllRecords()');

    const data = {
      ids: {},
      criteria: {
        match: 'all',
        rules: [],
        groups: [],
      },
      type: 'query',
      clear_all_records: true,
    };

    const options = {
      url: `objects/${objectKey}/records/delete`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createPaymentProcessor(data) {
    debug('createPaymentProcessor()');

    const options = {
      url: `applications/${this.application_id}/payment_processors`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async updatePaymentProcessor(payment_procesor_key, data) {
    debug('updatePaymentProcessor()');

    const options = {
      url: `applications/${this.application_id}/payment_processors/${payment_procesor_key}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async deletePaymentProcessor(payment_procesor_key) {
    debug('deletePaymentProcessor()');

    const options = {
      url: `applications/${this.application_id}/payment_processors/${payment_procesor_key}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async createField(object_key, name, type, required, unique, defaultValue, format, relationship, validation, fieldIndex, meta) {
    debug('createField()');

    const request_headers = { ...this.headers };

    request_headers['X-Knack-REST-API-Key'] = 'builder';

    let queryString = '';

    if (!_.isNil(fieldIndex)) {
      queryString = `?fieldIndex=${fieldIndex}`;
    }

    const data = {
      name,
      type,
      required,
      unique,
      default: defaultValue, // default reserved so the short syntax doesn't work
      format,
      relationship,
      meta,
    };

    this._removeEmptyAttributes(data);

    const options = {
      url: `objects/${object_key}/fields${queryString}`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createPaymentField(objectKey, paymentObjectKey) {
    debug('createPaymentField()');

    const data = {
      name: 'Payment',
      type: 'connection',
      charge_key: 'charge',
      ecommerce: true,
      immutable: true,
      object_key: objectKey,
      read_only: true,
      relationship: {
        belongs_to: 'one',
        has: 'one',
        object: paymentObjectKey,
      },
    };

    const options = {
      url: `objects/${objectKey}/fields`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createView(scene_key, data) {
    debug('createView()');

    const options = {
      url: `scenes/${scene_key}/views`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async updateViews(data) {
    debug('updateViews()');

    const options = {
      url: 'scenes/views/bulk',
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateView(scene_key, view_key, data) {
    debug('updateView()');

    const options = {
      url: `scenes/${scene_key}/views/${view_key}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async moveView(previous_scene_key, target_scene_key, view_key, completeViewSchema) {
    debug('moveView()');

    const data = {
      action: 'move',
      target_scene_key,
      view_key,
      completeViewSchema,
    };

    const options = {
      url: `scenes/${previous_scene_key}/copyview`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async copyView(previous_scene_key, target_scene_key, view_key, completeViewSchema) {
    debug('copyView()');

    const data = {
      action: 'copy',
      target_scene_key,
      view_key,
      completeViewSchema,
    };

    const options = {
      url: `scenes/${previous_scene_key}/copyview`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async deleteView(sceneKey, viewKey) {
    debug('deleteView()');

    const options = {
      url: `scenes/${sceneKey}/views/${viewKey}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async deleteField(object_key, field_key) {
    debug('deleteField()');

    const options = {
      url: `objects/${object_key}/fields/${field_key}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async updateField(object_key, field_key, name, type, required, unique, defaultValue, format, conditionalRules, validationRules, relationship, meta) {
    debug('updateField()');

    const data = {
      name,
      type,
      required,
      unique,
      default: defaultValue,
      format,
      rules: conditionalRules,
      validation: validationRules,
      relationship,
      meta,
    };

    data.conditional = !_.isEmpty(conditionalRules);

    this._removeEmptyAttributes(data);

    const options = {
      url: `objects/${object_key}/fields/${field_key}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateFieldsSort(objectKey, order) {
    debug('updateFieldsSort()');

    const options = {
      url: `objects/${objectKey}/fields/sort`,
      method: 'POST',
      data: {
        order,
      },
    };

    return this.axios(options);
  }

  // order is for v2 positioning
  // pageGroups is for v3 positioning
  async updateViewOrder(pageKey, order, pageGroups) {
    debug('updateViewOrder()');

    const options = {
      url: `scenes/${pageKey}/views/sort`,
      method: 'POST',
      data: {
        order,
        pageGroups,
      },
    };

    return this.axios(options);
  }

  async getObject(object_key) {
    debug('getObject()');

    const options = {
      url: `objects/${object_key}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getObjectFields(object_key) {
    debug('getObjectFields()');

    const options = {
      url: `objects/${object_key}/fields`,
      method: 'GET',
    };

    return this.axios(options);
  }

  /**
   * Creates a table
   *
   * @param {string} name
   * @param {string[]} fields
   * @param {["company","employee","contact","product","order"]} template
   * @param {boolean} user
   * @returns {Promise}
   */
  async createObject(name = '', fields = [], template = '', user = false) {
    debug('createObject()');

    const data = {
      name,
      fields,
      template,
      user,
    };

    const options = {
      url: 'objects',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  /**
  * Adds a set of objects to an application. 
  *
  * NOTE: This has been tested mostly with the AI application creation functionality and
  * may be not be ready for general usage.
  *
  * In connections fields, the target object is specified using the object name, 
  * because it may not exist yet.
  *
  * @param {Array<{
  *   name: string,
  *   fields: Array<{ name: string, type: string, required: boolean, unique: boolean, format: object}>,
  *   }>
  * }>} objects
  */
  async createObjects(objects) {
    debug('createObjects()');

    const data = {
      objects,
    };

    const options = {
      url: 'objects',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  /**
   * Creates one or more Tables from a list of templates.
   *
   * @param {["company","employee","contact","product","order"][]} templates
   * @returns {Promise}
   */
  async createObjectFromTemplates(templates = []) {
    debug('createObjectFromTemplates()');

    const data = {
      templates,
    };

    const options = {
      url: 'objects/templates',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async createPaymentMethodsObject() {
    debug('createPaymentMethodsObject()');

    const data = {
      name: 'Payment Methods',
      ecommerce_payment_methods: true,
      fields: [],
      conns: [],
    };

    const options = {
      url: 'objects',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async copyObject(objectKey, object_name, fields = [], target_object_id = null, user_role = false) {
    debug('copyObject()');

    const options = {
      url: `objects/${objectKey}/copy`,
      method: 'POST',
      data: {
        object_name,
        fields,
        target_object_id,
        user_role,
      },
    };

    return this.axios(options);
  }

  async updateObject(object_key, name, identifier, sort) {
    debug('updateObject()');

    const data = {
      name,
      identifier,
      sort,
    };

    this._removeEmptyAttributes(data);

    const options = {
      url: `objects/${object_key}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async deleteObject(object_key) {
    debug('deleteObject()');

    const options = {
      url: `objects/${object_key}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async updateObjectsSort(order) {
    debug('updateObjectsSort()');

    const options = {
      url: 'objects/sort',
      method: 'POST',
      data: {
        order,
      },
    };

    return this.axios(options);
  }

  async copyFields(object_key, target_object_id, target_object_name, fields, user_role = false) {
    debug('copyFields()');

    const options = {
      url: `objects/${object_key}/copy_fields`,
      method: 'POST',
      data: {
        target_object_id,
        object_name: target_object_name,
        fields,
        user_role,
      },
    };

    return this.axios(options);
  }

  /**
   * Create a new application
   *
   * @param {string | undefined} appName Application name
   * @param {string | undefined} origin For tracking purposes. It can be: 'scratch', 'import', 'template', 'ai', 'auto'.
   * @param {string | undefined} slug Slug for the application,
   * if not provided, a slug will be generated either from provided appName or from default App Name in Server repo.
   */
  async createApplication(appName = undefined, origin = undefined, slug = undefined) {
    debug('createApplication()');

    // TODO: not sure what this is or why it's necessary
    const existingCookies = await this._getCookies(this.api_uri);

    if (_.isEmpty(existingCookies)) {
      await this.authenticate();
    }

    const options = {
      url: `accounts/${this.account_id}/applications${origin ? `?origin=${origin}` : ''}`,
      method: 'POST',
      data: {
        name: appName,
        slug,
      },
      mode: 'dashboard',
    };

    const { application } = await this.axios(options);

    if (_.isNil(application)) {
      throw new Error('Failed to create application');
    }

    this._updateInternals(this.account_id, application.id, this.api_key);

    const api_key = await this.getApiKey();

    if (_.isNil(api_key)) {
      throw new Error('Failed to retrieve newly created application\'s api key.');
    }

    return {
      application,
      api_key,
    };
  }

  async createApplicationFromFixture(appName, fixture) {
    debug('createApplicationFromFixture()');

    const options = {
      url: `accounts/${this.account_id}/applications/fixture`,
      method: 'POST',
      data: {
        name: appName,
        fixture,
      },
    };

    const { application } = await this.axios(options);

    if (_.isNil(application)) {
      throw new Error('Failed to create application');
    }

    this._updateInternals(this.account_id, application.id, this.api_key);

    const api_key = await this.getApiKey();

    if (_.isNil(api_key)) {
      throw new Error('Failed to retrieve newly created application\'s api key.');
    }

    return {
      application,
      api_key,
    };
  }

  async deleteApplication(application_id) {
    debug('deleteApplication()');

    this.application_id = application_id || this.application_id;

    if (!this.account_id) {
      // TODO: we're throwing an error here but giving the consumer of this fn zero opportunity to supply it, why?
      throw new Error('account_id is required');
    }

    if (!this.application_id) {
      throw new Error('application_id is required as argument or doing wrapper construction');
    }

    const options = {
      url: `accounts/${this.account_id}/applications/${this.application_id}/delete`,
      method: 'POST',
      mode: 'dashboard',
    };

    const existingCookies = await this._getCookies(this.api_uri);

    if (_.isEmpty(existingCookies)) {
      await this.authenticate();
    }

    return this.axios(options);
  }

  async getApplication(application_id) {
    debug('getApplication()');

    this.application_id = application_id || this.application_id;

    if (!this.application_id) {
      throw new Error('application_id is required as argument or doing wrapper construction');
    }

    const options = {
      url: `applications/${this.application_id}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async listApplications(accountSlug) {
    debug('listApplications()');

    const options = {
      url: `${this.api_uri}dashboard/${accountSlug}/users/apps`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async updateApplication(settings) {
    debug('updateApplication()');

    const options = {
      url: 'settings',
      method: 'PUT',
      data: settings,
    };

    return this.axios(options);
  }

  /**
   * Creates a distribution/embed app
   * @param {string} number
   * @param {string} slug
   * @param {object} design
   */
  async createDistribution(name, slug, design) {
    debug('createDistribution()');

    const data = {
      name,
      scene: slug,
      design,
    };

    const options = {
      url: 'distributions',
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async deleteDistribution(distKey) {
    debug('deleteDistribution()');

    const options = {
      url: `distributions/${distKey}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  /**
   * Updates a distribution/embed app
   * @param {string} distKey
   * @param {string} name
   * @param {string} slug
   * @param {object} design
   */
  async updateDistribution(distKey, name, slug, design) {
    debug('updateDistribution()');

    const data = {
      name,
      scene: slug,
      design,
    };

    const options = {
      url: `distributions/${distKey}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateDistributionsSort(order) {
    debug('updateDistributionsSort()');

    const options = {
      url: 'distributions/sort',
      method: 'POST',
      data: {
        order,
      },
    };

    return this.axios(options);
  }

  async getInitialBuilderData(account_slug, application_slug) {
    debug('getInitialBuilderData()');

    if (_.isNil(account_slug)) {
      throw new Error('account_slug argument is required');
    }

    if (_.isNil(application_slug)) {
      throw new Error('application_slug argument is required');
    }

    const options = {
      url: `account/${account_slug}/application/${application_slug}/layout/builder`,
      method: 'GET',
    };

    const builder_layout_data = await this.axios(options);

    this.application_id = builder_layout_data.application.id;
    this.account_id = builder_layout_data.application.account.id;

    this._clientSubdomainMap = builder_layout_data.application.clientSubdomainMap;

    this._updateInternals(this.account_id, this.application_id);

    return builder_layout_data;
  }

  async uploadImageFromUrl(imageUrl) {
    debug('uploadImageFromUrl()');

    const options = {
      url: imageUrl,
      method: 'GET',
      responseType: 'arraybuffer',
    };

    const indexAfterLastSlash = imageUrl.lastIndexOf('/') + 1;
    const filename = imageUrl.substring(indexAfterLastSlash, imageUrl.length);
    const buffer = await this.axios(options);

    return this._uploadAsset('image', buffer, filename);
  }

  async uploadImageFromFile(assetFilePath) {
    debug('uploadImageFromFile()');

    return this._uploadAssetFromFile('image', assetFilePath);
  }

  async uploadFileFromFile(assetFilePath) {
    debug('uploadFileFromFile()');

    return this._uploadAssetFromFile('file', assetFilePath);
  }

  async getGoogleSheetsPreview(spreadsheetId, token) {
    debug('getGoogleSheetsPreview()');

    if (_.isNil(spreadsheetId)) {
      throw new Error('spreadsheetId is required');
    }

    if (_.isNil(token)) {
      throw new Error('token is required');
    }

    const form = new FormData();

    form.append('spreadsheetId', spreadsheetId);
    form.append('token', token);

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    const options = {
      url: `import/google-sheets/preview`,
      method: 'POST',
      data: form,
    };

    return await this.axios(options);
  }

  async importGoogleSheetsFile(importAction = 'update', hasHeaderRow, match, mappedFields, newFields, objectKey = null, objectName = null, file, defaultValues, isUser = false, token, selectedSheetIndex, skipRecordsWithErrors = false) {
    debug('importGoogleSheetsFile()');
    if (_.isNil(file)) {
      throw new Error('file is required');
    }

    if (_.isNil(token)) {
      throw new Error('token is required');
    }

    if (_.isNil(selectedSheetIndex)) {
      throw new Error('selectedSheetIndex is required');
    }

    const form = new FormData();

    form.append('importAction', importAction);
    form.append('hasHeaderRow', hasHeaderRow);
    form.append('match', JSON.stringify(match));
    form.append('mappedFields', JSON.stringify(mappedFields));
    form.append('newFields', JSON.stringify(newFields));
    form.append('importFile', file);
    form.append('defaultValues', JSON.stringify(defaultValues));
    form.append('isUser', isUser);
    form.append('spreadsheetId', file.id)
    form.append('selectedSheetIndex', selectedSheetIndex)
    form.append('token', token)
    form.append('skipRecordsWithErrors', skipRecordsWithErrors);

    if (objectKey) {
      form.append('objectKey', objectKey);
    }

    if (objectName) {
      form.append('objectName', objectName);
    }

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    const options = {
      url: 'import/google-sheets',
      method: 'POST',
      data: form,
    };

    return await this.axios(options);
  }

  async uploadWebFileForImport(importAction = 'update', hasHeaderRow = true, match, mappedFields, newFields, objectKey = null, objectName = null, file, defaultValues = [], isUser = false, token, selectedSheetIndex = null, skipRecordsWithErrors = false) {
    debug('uploadWebFileForImport()');

    const form = new FormData();
    form.append('importAction', importAction);
    form.append('hasHeaderRow', hasHeaderRow);
    form.append('match', JSON.stringify(match));
    form.append('mappedFields', JSON.stringify(mappedFields));
    form.append('newFields', JSON.stringify(newFields));
    form.append('importFile', file);
    form.append('defaultValues', JSON.stringify(defaultValues));
    form.append('isUser', isUser);
    form.append('skipRecordsWithErrors', skipRecordsWithErrors);

    if (objectKey) {
      form.append('objectKey', objectKey);
    }

    if (objectName) {
      form.append('objectName', objectName);
    }

    if (selectedSheetIndex) {
      form.append('selectedSheetIndex', selectedSheetIndex);
    }

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    const options = {
      url: 'import/start',
      method: 'POST',
      data: form,
    };

    return this.axios(options);
  }

  async uploadWebFileForLogo(asset, ssoName = false) {
    debug('uploadWebFileForLogo()');

    const { content, filename } = asset;

    const form = new FormData();

    form.append('files', content, filename);

    if (ssoName) {
      form.append('sso', ssoName);
    }

    const options = {
      url: `/applications/${this.application_id}/users/${this.user_id}/logo/swfupload`,
      method: 'POST',
      data: form,
    };

    return this.axios(options);
  }

  // Currently unusable by browser-based solutions due to FormData being from
  // https://developer.mozilla.org/en-US/docs/Web/API/FormData
  // and not the form-data npm module which has a getHeaders function to setup the proper
  // headers (e.g., content-type: multipart/form-data boundary=----WebKitFormBoundaryt5wu2U3rver5Xa0S)
  async uploadStringContentForImport(importType = 'create', headerRow = 1, matchField = 'bacon', objectKey, fieldName, fileName, content) {
    debug('uploadStringContentForImport()');

    const form = new FormData();

    form.append('input_headers', headerRow === 1 ? 'row_1' : (headerRow === 2 ? 'row_2' : 'none'));
    form.append('match_field', matchField);
    form.append('type', importType);
    form.append('object_key', objectKey);

    form.append('files', content, {
      filename: fileName,
    });

    const options = {
      url: `applications/${this.application_id}/import/swfupload`,
      method: 'POST',
      data: form,
      resolveWithFullResponse: true,
    };

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    return this.axios(options);
  }

  async _uploadAssetFromFile(assetType, assetFilePath) {
    debug('_uploadAssetFromFile()');

    const indexAfterLastSlash = assetFilePath.lastIndexOf('/') + 1;
    const filename = assetFilePath.substring(indexAfterLastSlash, assetFilePath.length);
    const buffer = await readFile(assetFilePath);

    return this._uploadAsset(assetType, buffer, filename);
  }

  async _uploadAsset(assetType, buffer, filename) {
    debug('_uploadAsset()');

    const form = new FormData();

    form.append('files', buffer, {
      filename,
    });

    const options = {
      url: `applications/${this.application_id}/assets/${assetType}/upload`,
      method: 'POST',
      data: form,
    };

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    return this.axios(options);
  }

  async downloadAsset(asset_id, filename) {
    debug('downloadAsset()');

    const options = {
      url: `applications/${this.application_id}/download/asset/${asset_id}/${filename}`, // TODO this route requires modification: asset-controller.lookupAsset can't set file type by default if we want image support here
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async getAccountTemplate(profileKey, templateType) {
    debug('getAccountTemplate()');

    const options = {
      url: `profiles/${profileKey}/user_templates/${templateType}`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async updateAccountTemplate(profileKey, templateType, template) {
    debug('updateAccountTemplate()');

    const options = {
      url: `profiles/${profileKey}/user_templates/${templateType}`,
      method: 'POST',
      encoding: null,
      data: {
        template,
      },
    };

    return this.axios(options);
  }

  async createTask(objectKey, data) {
    debug('createTask()');

    const options = {
      url: `objects/${objectKey}/tasks`,
      method: 'POST',
      data,
    };

    return this.axios(options);
  }

  async updateTask(objectKey, taskKey, data) {
    debug('updateTask()');

    const options = {
      url: `objects/${objectKey}/tasks/${taskKey}`,
      method: 'PUT',
      data,
    };

    return this.axios(options);
  }

  async updateTasksSort(objectKey, order) {
    debug('updateTasksSort()');

    const options = {
      url: `objects/${objectKey}/tasks/sort`,
      method: 'POST',
      data: {
        order,
      },
    };

    return this.axios(options);
  }

  async deleteTask(objectKey, taskKey) {
    debug('deleteTask()');

    const options = {
      url: `objects/${objectKey}/tasks/${taskKey}`,
      method: 'DELETE',
    };

    return this.axios(options);
  }

  async getEmailMessage(messageId) {
    const options = {
      url: `applications/${this.application_id}/mail/${messageId}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getEmailDeliveryHistory(key) {
    // key can be either a task key or a view key
    const options = {
      url: `applications/${this.application_id}/mail/history/${key}`,
      method: 'GET',
    };

    return this.axios(options);
  }

  async getJobLogs(applicationId, batchId) {
    const options = {
      url: `applications/${applicationId}/logs/${batchId}`,
      method: 'GET',
    };
    return this.axios(options);
  }

  async getSQLRecordHistory(objectKey, recordId, pageNumber = 1) {
    debug('getSQLRecordHistory()');

    const options = {
      url: `applications/${this.application_id}/objects/${objectKey}/record-history/${recordId}?page=${pageNumber}`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async restoreRecordToHistoryPoint(objectKey, recordId, historyId) {
    debug('restoreRecordToHistoryPoint');

    const options = {
      url: `applications/${this.application_id}/objects/${objectKey}/record-history/${recordId}/restore/${historyId}`,
      method: 'PUT',
      encoding: null,
    };
    return this.axios(options);
  }

  async getTaskHistory(objectKey, taskKey) {
    debug('getTaskHistory()');

    const options = {
      url: `applications/${this.application_id}/joblogs/?task_key=${taskKey}&object_key=${objectKey}&limit=0&sort=-1`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async runTask(taskKey) {
    debug('runTask()');

    const options = {
      url: `applications/${this.application_id}/tasks/${taskKey}/runtaskjob`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async getAssetInfo(assetId) {
    const options = {
      url: `applications/${this.application_id}/asset/${assetId}`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async getAssetMetadata(assetId, pageKey, viewKey, fieldKey) {
    debug('getAssetMetadata()');

    const options = {
      url: `applications/${this.application_id}/scene/${pageKey}/view/${viewKey}/field/${fieldKey}/asset/${assetId}`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async getAssetSignedUrl(assetId, pageKey, viewKey, fieldKey, isPreview = false) {
    debug('getAssetSignedUrl()');

    const options = {
      url: `applications/${this.application_id}/scene/${pageKey}/view/${viewKey}/field/${fieldKey}/assets/${assetId}/signed-url?preview=${!!isPreview}`,
      method: 'GET',
      encoding: null,
    };

    return this.axios(options);
  }

  async createStaticAsset(userId, filePath, sso) {
    debug('createStaticAsset()');

    const form = new FormData();

    const filename = _.last(filePath.split('/'));

    const buffer = await readFile(filePath);

    form.append('files', buffer, {
      filename,
    });

    if (!_.isNil(sso)) {
      form.append('sso', sso);
    }

    const options = {
      url: `applications/${this.application_id}/users/${userId}/logo/swfupload`,
      method: 'POST',
      data: form,
    };

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    return this.axios(options);
  }

  async getStaticAssets(params) {
    debug('getStaticAssets()');

    const options = {
      url: `applications/${this.application_id}/assets/static`,
      method: 'GET',
      params,
    };

    return this.axios(options);
  }

  async updateStaticAsset(userId, filePath, sso) {
    debug('updateStaticAsset()');

    const form = new FormData();

    const filename = _.last(filePath.split('/'));

    const buffer = await readFile(filePath);

    form.append('files', buffer, {
      filename,
    });

    if (!_.isNil(sso)) {
      form.append('sso', sso);
    }

    const options = {
      url: `applications/${this.application_id}/users/${userId}/logo`,
      method: 'PUT',
      data: form,
    };

    if (_.isFunction(form.getHeaders)) {
      options.headers = { ...form.getHeaders() };
    }

    return this.axios(options);
  }

  async removeStaticAsset(userId, sso) {
    debug('removeStaticAsset()');

    const params = {};

    if (!_.isNil(sso)) {
      params.sso = sso;
    }

    const options = {
      url: `applications/${this.application_id}/users/${userId}/logo/remove`,
      method: 'GET',
      encoding: null,
      params,
    };

    return this.axios(options);
  }

  async getApplicationMapsUsage() {
    debug('getApplicationMapsUsage()');

    const options = {
      url: `applications/${this.application_id}/maps/usage`,
      method: 'GET',
    };

    return this.axios(options);
  }

  /**
   * Searches the help documents for the given query.
   *
   * @param {string} query
   * @param {number} limit
   * @returns {Promise}
   */
  async helpSearch(query, limit) {
    debug('helpSearch()');

    const options = {
      url: `applications/${this.application_id}/help/search-help`,
      method: 'GET',
      params: {
        q: query || '',
        num: Number(limit) || undefined,
      },
    };

    return this.axios(options);
  }

  /**
   * Sends a support ticket (help request) to zendesk.
   *
   * @param {Object} ticketData
   * @returns {Promise}
   */
  async helpGetHelp(ticketData) {
    const requestOptions = {
      url: `applications/${this.application_id}/help/get-help`,
      method: 'POST',
      data: ticketData,
    };

    return this.axios(requestOptions);
  }

  /**
   * Sends a bug report to zendesk.
   *
   * @param {Object} ticketData
   * @returns {Promise}
   */
  async helpReportBug(ticketData) {
    const requestOptions = {
      url: `applications/${this.application_id}/help/report-bug`,
      method: 'POST',
      data: ticketData,
    };

    return this.axios(requestOptions);
  }

  /**
   * Sends a feedback report to zendesk.
   *
   * @param {Object} ticketData
   * @returns {Promise}
   */
  async helpShareFeedback(ticketData) {
    const requestOptions = {
      url: `applications/${this.application_id}/help/share-feedback`,
      method: 'POST',
      data: ticketData,
    };

    return this.axios(requestOptions);
  }

  /**
   * Sends an emergency report to zendesk.
   *
   * @param {Object} ticketData
   * @returns {Promise}
   */
  async helpReportEmergency(ticketData) {
    const requestOptions = {
      url: `applications/${this.application_id}/help/report-emergency`,
      method: 'POST',
      data: ticketData,
    };

    return this.axios(requestOptions);
  }

  /**
   * Sets the data source for the application.
   *
   * @param {object} setData
   * @param {string} setData.previewDataSource
   * @returns {Promise}
   */
  async applicationSettingDataSourceSet({ previewDataSource }) {
    const requestOptions = {
      url: `applications/${this.application_id}/settings/data-source`,
      method: 'POST',
      data: {
        previewDataSource,
      },
    };

    return this.axios(requestOptions);
  }
};
